import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Plus, Search, BookOpen, MoreHorizontal, Trash2, Copy, Play, Eye,
  CheckCircle2, Clock, AlertTriangle, XCircle, ArrowLeft,
  ChevronRight, Shield, Wrench, AlertCircle, Users,
  GripVertical,
} from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Textarea } from '@/components/ui/textarea'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '@/components/ui/dialog'
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import { apiClient } from '@/lib/api-client'
import { useToast } from '@/hooks/use-toast'

// ============================================
// Types
// ============================================

interface PlaybookStep {
  title: string
  description?: string
  type: string
  assignee?: string
  estimatedMinutes?: number
  required: boolean
  order: number
}

interface StepResult {
  stepIndex: number
  status: string
  completedAt?: string | null
  completedBy?: string | null
  notes?: string
  duration?: number
}

interface Playbook {
  id: string
  name: string
  description: string | null
  category: string
  severity: string | null
  triggerType: string | null
  steps: PlaybookStep[]
  isActive: boolean
  version: number
  lastUsedAt: string | null
  createdAt: string
  updatedAt: string
  _count?: { executions: number }
  executions?: PlaybookExecution[]
}

interface PlaybookExecution {
  id: string
  playbookId: string
  executedBy: string
  status: string
  stepResults: StepResult[] | null
  startedAt: string
  completedAt: string | null
  notes: string | null
  playbook?: { name: string; category: string; severity: string | null; steps?: PlaybookStep[] }
}

// ============================================
// Category/Severity Helpers
// ============================================

const CATEGORIES = [
  { value: 'incident', label: 'Incident', icon: AlertTriangle },
  { value: 'security', label: 'Security', icon: Shield },
  { value: 'disaster_recovery', label: 'Disaster Recovery', icon: AlertCircle },
  { value: 'maintenance', label: 'Maintenance', icon: Wrench },
  { value: 'onboarding', label: 'Onboarding', icon: Users },
]

const SEVERITIES = [
  { value: 'low', label: 'Low', color: 'text-green-500' },
  { value: 'medium', label: 'Medium', color: 'text-yellow-500' },
  { value: 'high', label: 'High', color: 'text-orange-500' },
  { value: 'critical', label: 'Critical', color: 'text-red-500' },
]

function CategoryBadge({ category }: { category: string }) {
  const cat = CATEGORIES.find((c) => c.value === category)
  return (
    <Badge variant="outline" className="gap-1">
      {cat && <cat.icon className="h-3 w-3" />}
      {cat?.label || category}
    </Badge>
  )
}

function SeverityBadge({ severity }: { severity: string | null }) {
  if (!severity) return null
  const sev = SEVERITIES.find((s) => s.value === severity)
  return (
    <Badge variant={
      severity === 'critical' ? 'destructive' :
      severity === 'high' ? 'destructive' :
      severity === 'medium' ? 'secondary' : 'outline'
    }>
      {sev?.label || severity}
    </Badge>
  )
}

function ExecutionStatusBadge({ status }: { status: string }) {
  switch (status) {
    case 'running':
      return <Badge variant="secondary" className="gap-1"><Clock className="h-3 w-3" /> Running</Badge>
    case 'completed':
      return <Badge variant="default" className="gap-1"><CheckCircle2 className="h-3 w-3" /> Completed</Badge>
    case 'failed':
      return <Badge variant="destructive" className="gap-1"><XCircle className="h-3 w-3" /> Failed</Badge>
    case 'cancelled':
      return <Badge variant="outline" className="gap-1"><XCircle className="h-3 w-3" /> Cancelled</Badge>
    default:
      return <Badge variant="outline">{status}</Badge>
  }
}

// ============================================
// Create Playbook Dialog
// ============================================

function CreatePlaybookDialog() {
  const [open, setOpen] = useState(false)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [category, setCategory] = useState('incident')
  const [severity, setSeverity] = useState('medium')
  const [triggerType, setTriggerType] = useState('manual')
  const [steps, setSteps] = useState<PlaybookStep[]>([
    { title: '', description: '', type: 'action', required: true, order: 0 },
  ])
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const createMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => apiClient.post('/playbooks', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['playbooks'] })
      toast({ title: 'Playbook created', description: 'Playbook created successfully' })
      setOpen(false)
      resetForm()
    },
    onError: () => toast({ title: 'Error', description: 'Failed to create playbook', variant: 'destructive' }),
  })

  function resetForm() {
    setName('')
    setDescription('')
    setCategory('incident')
    setSeverity('medium')
    setTriggerType('manual')
    setSteps([{ title: '', description: '', type: 'action', required: true, order: 0 }])
  }

  function addStep() {
    setSteps([...steps, { title: '', description: '', type: 'action', required: true, order: steps.length }])
  }

  function updateStep(index: number, field: string, value: unknown) {
    const updated = [...steps]
    updated[index] = { ...updated[index], [field]: value }
    setSteps(updated)
  }

  function removeStep(index: number) {
    if (steps.length <= 1) return
    const updated = steps.filter((_, i) => i !== index).map((s, i) => ({ ...s, order: i }))
    setSteps(updated)
  }

  function moveStep(index: number, direction: 'up' | 'down') {
    if ((direction === 'up' && index === 0) || (direction === 'down' && index === steps.length - 1)) return
    const updated = [...steps]
    const swapIndex = direction === 'up' ? index - 1 : index + 1
    const temp = updated[index]
    updated[index] = updated[swapIndex]
    updated[swapIndex] = temp
    setSteps(updated.map((s, i) => ({ ...s, order: i })))
  }

  function handleCreate() {
    const validSteps = steps.filter((s) => s.title.trim())
    if (!name || validSteps.length === 0) return
    createMutation.mutate({
      name,
      description: description || undefined,
      category,
      severity: severity || undefined,
      triggerType: triggerType || undefined,
      steps: validSteps,
    })
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button><Plus className="mr-2 h-4 w-4" /> New Playbook</Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Playbook</DialogTitle>
          <DialogDescription>Define steps for an incident response or operational playbook.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Playbook Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Server Down - Critical Response" />
          </div>
          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Describe the playbook purpose..." />
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Category</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => (
                    <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Severity</Label>
              <Select value={severity} onValueChange={setSeverity}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SEVERITIES.map((s) => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Trigger</Label>
              <Select value={triggerType} onValueChange={setTriggerType}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="manual">Manual</SelectItem>
                  <SelectItem value="alert">Alert</SelectItem>
                  <SelectItem value="schedule">Scheduled</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Step Builder */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Steps</Label>
              <Button variant="ghost" size="sm" onClick={addStep}><Plus className="mr-1 h-3 w-3" /> Add Step</Button>
            </div>
            <div className="space-y-3">
              {steps.map((step, i) => (
                <div key={i} className="flex gap-2 items-start border rounded-md p-3">
                  <div className="flex flex-col items-center gap-1 pt-2">
                    <GripVertical className="h-4 w-4 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground font-mono">{i + 1}</span>
                    <div className="flex flex-col gap-0.5">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-5 w-5"
                        onClick={() => moveStep(i, 'up')}
                        disabled={i === 0}
                      >
                        <ChevronRight className="h-3 w-3 -rotate-90" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-5 w-5"
                        onClick={() => moveStep(i, 'down')}
                        disabled={i === steps.length - 1}
                      >
                        <ChevronRight className="h-3 w-3 rotate-90" />
                      </Button>
                    </div>
                  </div>
                  <div className="flex-1 space-y-2">
                    <Input
                      value={step.title}
                      onChange={(e) => updateStep(i, 'title', e.target.value)}
                      placeholder={`Step ${i + 1} title`}
                    />
                    <Input
                      value={step.description || ''}
                      onChange={(e) => updateStep(i, 'description', e.target.value)}
                      placeholder="Step description (optional)"
                      className="text-sm"
                    />
                    <Select value={step.type} onValueChange={(v) => updateStep(i, 'type', v)}>
                      <SelectTrigger className="w-40">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="action">Action</SelectItem>
                        <SelectItem value="decision">Decision</SelectItem>
                        <SelectItem value="notification">Notification</SelectItem>
                        <SelectItem value="verification">Verification</SelectItem>
                        <SelectItem value="documentation">Documentation</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => removeStep(i)}
                    disabled={steps.length <= 1}
                    className="shrink-0"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleCreate} disabled={!name || !steps.some((s) => s.title.trim()) || createMutation.isPending}>
            {createMutation.isPending ? 'Creating...' : 'Create Playbook'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ============================================
// Playbook Detail View
// ============================================

function PlaybookDetail({
  playbook,
  onBack,
}: {
  playbook: Playbook
  onBack: () => void
}) {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const { data: detailData, isLoading } = useQuery({
    queryKey: ['playbook', playbook.id],
    queryFn: () => apiClient.get<Playbook>(`/playbooks/${playbook.id}`),
  })

  const executeMutation = useMutation({
    mutationFn: () => apiClient.post(`/playbooks/${playbook.id}/execute`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['playbook', playbook.id] })
      queryClient.invalidateQueries({ queryKey: ['playbook-executions'] })
      toast({ title: 'Execution started', description: `Playbook "${playbook.name}" is now running` })
    },
    onError: () => toast({ title: 'Error', description: 'Failed to start execution', variant: 'destructive' }),
  })

  const detail = detailData?.data || playbook
  const steps = (detail.steps || []) as PlaybookStep[]
  const recentExecutions = detail.executions || []

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-4 w-full" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="mr-2 h-4 w-4" /> Back
        </Button>
        <div className="flex-1">
          <h2 className="text-2xl font-bold tracking-tight">{detail.name}</h2>
          <p className="text-muted-foreground">{detail.description}</p>
        </div>
        <div className="flex gap-2">
          <CategoryBadge category={detail.category} />
          <SeverityBadge severity={detail.severity} />
          <Badge variant="outline">v{detail.version}</Badge>
        </div>
      </div>

      <div className="flex gap-2">
        <Button onClick={() => executeMutation.mutate()} disabled={executeMutation.isPending}>
          <Play className="mr-2 h-4 w-4" />
          {executeMutation.isPending ? 'Starting...' : 'Execute Playbook'}
        </Button>
      </div>

      {/* Steps */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Steps ({steps.length})</CardTitle>
          <CardDescription>Follow these steps in order when executing this playbook.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {steps
              .sort((a, b) => a.order - b.order)
              .map((step, index) => (
                <div key={index} className="flex items-start gap-3 p-3 rounded-lg border">
                  <div className="flex items-center justify-center h-7 w-7 rounded-full bg-primary text-primary-foreground text-xs font-bold shrink-0">
                    {index + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-medium">{step.title}</p>
                      <Badge variant="outline" className="text-xs">{step.type}</Badge>
                      {step.required && <Badge variant="secondary" className="text-xs">Required</Badge>}
                    </div>
                    {step.description && (
                      <p className="text-sm text-muted-foreground mt-1">{step.description}</p>
                    )}
                    <div className="flex gap-3 mt-1 text-xs text-muted-foreground">
                      {step.assignee && <span>Assignee: {step.assignee}</span>}
                      {step.estimatedMinutes && <span>~{step.estimatedMinutes}min</span>}
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </CardContent>
      </Card>

      {/* Recent Executions */}
      {recentExecutions.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Recent Executions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {recentExecutions.map((exec) => (
                <div key={exec.id} className="flex items-center justify-between p-3 rounded-lg border">
                  <div className="flex items-center gap-3">
                    <ExecutionStatusBadge status={exec.status} />
                    <span className="text-sm text-muted-foreground">
                      Started {new Date(exec.startedAt).toLocaleString()}
                    </span>
                  </div>
                  {exec.completedAt && (
                    <span className="text-xs text-muted-foreground">
                      Completed {new Date(exec.completedAt).toLocaleString()}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Metadata */}
      <div className="grid grid-cols-2 gap-4 text-sm text-muted-foreground">
        <p>Created: {new Date(detail.createdAt).toLocaleDateString()}</p>
        <p>Last updated: {new Date(detail.updatedAt).toLocaleDateString()}</p>
        {detail.lastUsedAt && <p>Last used: {new Date(detail.lastUsedAt).toLocaleDateString()}</p>}
        {detail.triggerType && <p>Trigger: {detail.triggerType}</p>}
      </div>
    </div>
  )
}

// ============================================
// Execution Detail View
// ============================================

function ExecutionDetail({
  executionId,
  onBack,
}: {
  executionId: string
  onBack: () => void
}) {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const { data: execData, isLoading } = useQuery({
    queryKey: ['playbook-execution', executionId],
    queryFn: () => apiClient.get<PlaybookExecution>(`/playbooks/executions/${executionId}`),
  })

  const updateMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      apiClient.patch(`/playbooks/executions/${executionId}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['playbook-execution', executionId] })
      queryClient.invalidateQueries({ queryKey: ['playbook-executions'] })
    },
    onError: () => toast({ title: 'Error', description: 'Failed to update execution', variant: 'destructive' }),
  })

  if (isLoading) {
    return <div className="space-y-4"><Skeleton className="h-8 w-48" /><Skeleton className="h-4 w-full" /></div>
  }

  const execution = execData?.data
  if (!execution) return null

  const steps = (execution.playbook?.steps || []) as PlaybookStep[]
  const results = (execution.stepResults || []) as StepResult[]

  function toggleStep(stepIndex: number) {
    const currentResult = results.find((r) => r.stepIndex === stepIndex)
    const isCompleted = currentResult?.status === 'completed'
    const updatedResults = results.map((r) => {
      if (r.stepIndex === stepIndex) {
        return {
          ...r,
          status: isCompleted ? 'pending' : 'completed',
          completedAt: isCompleted ? null : new Date().toISOString(),
        }
      }
      return r
    })

    // Check if all steps are completed
    const allCompleted = updatedResults.every((r) => r.status === 'completed' || r.status === 'skipped')

    updateMutation.mutate({
      stepResults: updatedResults,
      ...(allCompleted ? { status: 'completed' } : {}),
    })
  }

  function completeExecution() {
    updateMutation.mutate({ status: 'completed' })
  }

  function cancelExecution() {
    updateMutation.mutate({ status: 'cancelled' })
  }

  const completedSteps = results.filter((r) => r.status === 'completed').length
  const totalSteps = results.length
  const progressPct = totalSteps > 0 ? Math.round((completedSteps / totalSteps) * 100) : 0

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="mr-2 h-4 w-4" /> Back
        </Button>
        <div className="flex-1">
          <h2 className="text-2xl font-bold tracking-tight">{execution.playbook?.name}</h2>
          <p className="text-muted-foreground">
            Execution started {new Date(execution.startedAt).toLocaleString()}
          </p>
        </div>
        <ExecutionStatusBadge status={execution.status} />
      </div>

      {/* Progress */}
      <div className="space-y-2">
        <div className="flex justify-between text-sm">
          <span>{completedSteps}/{totalSteps} steps completed</span>
          <span>{progressPct}%</span>
        </div>
        <div className="w-full bg-muted rounded-full h-3">
          <div
            className={`h-3 rounded-full transition-all ${
              progressPct === 100 ? 'bg-green-500' :
              progressPct > 50 ? 'bg-blue-500' :
              progressPct > 0 ? 'bg-yellow-500' : 'bg-muted-foreground/20'
            }`}
            style={{ width: `${progressPct}%` }}
          />
        </div>
      </div>

      {execution.status === 'running' && (
        <div className="flex gap-2">
          <Button variant="default" onClick={completeExecution} disabled={updateMutation.isPending}>
            <CheckCircle2 className="mr-2 h-4 w-4" /> Mark Complete
          </Button>
          <Button variant="outline" onClick={cancelExecution} disabled={updateMutation.isPending}>
            <XCircle className="mr-2 h-4 w-4" /> Cancel
          </Button>
        </div>
      )}

      {/* Step Progress */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Step-by-Step Progress</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {steps
              .sort((a, b) => a.order - b.order)
              .map((step, index) => {
                const result = results.find((r) => r.stepIndex === index)
                const isCompleted = result?.status === 'completed'
                const isRunning = execution.status === 'running'

                return (
                  <div
                    key={index}
                    className={`flex items-start gap-3 p-3 rounded-lg border transition-colors ${
                      isCompleted ? 'bg-muted/50 border-muted' : 'hover:bg-accent/50'
                    }`}
                  >
                    {isRunning ? (
                      <Checkbox
                        checked={isCompleted}
                        onCheckedChange={() => toggleStep(index)}
                        className="mt-0.5"
                      />
                    ) : (
                      <div className={`mt-0.5 ${isCompleted ? 'text-green-500' : 'text-muted-foreground'}`}>
                        {isCompleted ? <CheckCircle2 className="h-4 w-4" /> : <Clock className="h-4 w-4" />}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-mono text-muted-foreground">{index + 1}.</span>
                        <p className={`font-medium ${isCompleted ? 'line-through text-muted-foreground' : ''}`}>
                          {step.title}
                        </p>
                        <Badge variant="outline" className="text-xs">{step.type}</Badge>
                      </div>
                      {step.description && (
                        <p className="text-sm text-muted-foreground mt-1">{step.description}</p>
                      )}
                      {result?.completedAt && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Completed {new Date(result.completedAt).toLocaleString()}
                        </p>
                      )}
                    </div>
                  </div>
                )
              })}
          </div>
        </CardContent>
      </Card>

      {execution.notes && (
        <Card>
          <CardHeader><CardTitle className="text-lg">Notes</CardTitle></CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground whitespace-pre-wrap">{execution.notes}</p>
          </CardContent>
        </Card>
      )}
    </div>
  )
}

// ============================================
// Main Playbooks Page
// ============================================

export function PlaybooksPage() {
  const [search, setSearch] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('all')
  const [severityFilter, setSeverityFilter] = useState('all')
  const [activeTab, setActiveTab] = useState('library')
  const [selectedPlaybook, setSelectedPlaybook] = useState<Playbook | null>(null)
  const [selectedExecutionId, setSelectedExecutionId] = useState<string | null>(null)
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const { data: playbookData, isLoading: playbooksLoading } = useQuery({
    queryKey: ['playbooks', categoryFilter, severityFilter],
    queryFn: () => apiClient.get<{ playbooks: Playbook[]; pagination: unknown }>('/playbooks', {
      limit: 100,
      ...(categoryFilter !== 'all' ? { category: categoryFilter } : {}),
      ...(severityFilter !== 'all' ? { severity: severityFilter } : {}),
    }),
  })

  const { data: executionData, isLoading: executionsLoading } = useQuery({
    queryKey: ['playbook-executions'],
    queryFn: () => apiClient.get<{ executions: PlaybookExecution[]; pagination: unknown }>('/playbooks/executions', { limit: 50 }),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/playbooks/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['playbooks'] })
      toast({ title: 'Deleted', description: 'Playbook removed' })
    },
  })

  const duplicateMutation = useMutation({
    mutationFn: (id: string) => apiClient.post(`/playbooks/${id}/duplicate`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['playbooks'] })
      toast({ title: 'Duplicated', description: 'Playbook copy created' })
    },
    onError: () => toast({ title: 'Error', description: 'Failed to duplicate playbook', variant: 'destructive' }),
  })

  const executeMutation = useMutation({
    mutationFn: (id: string) => apiClient.post(`/playbooks/${id}/execute`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['playbooks'] })
      queryClient.invalidateQueries({ queryKey: ['playbook-executions'] })
      toast({ title: 'Execution started', description: 'Playbook is now running' })
    },
    onError: () => toast({ title: 'Error', description: 'Failed to start execution', variant: 'destructive' }),
  })

  const playbooks = playbookData?.data?.playbooks || []
  const executions = executionData?.data?.executions || []

  const filtered = playbooks.filter((p) =>
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    p.description?.toLowerCase().includes(search.toLowerCase())
  )

  // If viewing a specific execution
  if (selectedExecutionId) {
    return (
      <ExecutionDetail
        executionId={selectedExecutionId}
        onBack={() => setSelectedExecutionId(null)}
      />
    )
  }

  // If viewing a specific playbook
  if (selectedPlaybook) {
    return (
      <PlaybookDetail
        playbook={selectedPlaybook}
        onBack={() => setSelectedPlaybook(null)}
      />
    )
  }

  // Stats
  const activePlaybooks = playbooks.filter((p) => p.isActive).length
  const runningExecutions = executions.filter((e) => e.status === 'running').length
  const totalExecutions = executions.length

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Playbooks</h1>
          <p className="text-muted-foreground">Incident response and operational playbooks</p>
        </div>
        <CreatePlaybookDialog />
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-muted-foreground" />
              <div>
                <div className="text-2xl font-bold">{playbooks.length}</div>
                <p className="text-xs text-muted-foreground">Total Playbooks</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-green-500" />
              <div>
                <div className="text-2xl font-bold">{activePlaybooks}</div>
                <p className="text-xs text-muted-foreground">Active</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-blue-500" />
              <div>
                <div className="text-2xl font-bold text-blue-500">{runningExecutions}</div>
                <p className="text-xs text-muted-foreground">Running Now</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <Play className="h-5 w-5 text-purple-500" />
              <div>
                <div className="text-2xl font-bold">{totalExecutions}</div>
                <p className="text-xs text-muted-foreground">Total Executions</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Search playbooks..." className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-48"><SelectValue placeholder="Category" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {CATEGORIES.map((c) => (
              <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={severityFilter} onValueChange={setSeverityFilter}>
          <SelectTrigger className="w-40"><SelectValue placeholder="Severity" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Severities</SelectItem>
            {SEVERITIES.map((s) => (
              <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="library">Playbook Library</TabsTrigger>
          <TabsTrigger value="executions">Recent Executions</TabsTrigger>
        </TabsList>

        {/* Library Tab */}
        <TabsContent value="library">
          {playbooksLoading ? (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {[...Array(6)].map((_, i) => (
                <Card key={i}><CardHeader><Skeleton className="h-5 w-32" /></CardHeader><CardContent><Skeleton className="h-4 w-full" /></CardContent></Card>
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center py-12">
                <BookOpen className="h-12 w-12 mb-4 opacity-50 text-muted-foreground" />
                <p className="text-lg font-medium text-muted-foreground">No playbooks found</p>
                <p className="text-sm text-muted-foreground mt-1">Create your first playbook to get started.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {filtered.map((playbook) => {
                const steps = (playbook.steps || []) as PlaybookStep[]
                return (
                  <Card key={playbook.id} className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setSelectedPlaybook(playbook)}>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <BookOpen className="h-4 w-4 text-muted-foreground shrink-0" />
                        <CardTitle className="text-base font-medium truncate">{playbook.name}</CardTitle>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                          <Button variant="ghost" size="icon"><MoreHorizontal className="h-4 w-4" /></Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setSelectedPlaybook(playbook); }}>
                            <Eye className="mr-2 h-4 w-4" /> View
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); executeMutation.mutate(playbook.id); }}>
                            <Play className="mr-2 h-4 w-4" /> Execute
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); duplicateMutation.mutate(playbook.id); }}>
                            <Copy className="mr-2 h-4 w-4" /> Duplicate
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem className="text-destructive" onClick={(e) => { e.stopPropagation(); deleteMutation.mutate(playbook.id); }}>
                            <Trash2 className="mr-2 h-4 w-4" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </CardHeader>
                    <CardContent>
                      {playbook.description && (
                        <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{playbook.description}</p>
                      )}
                      <div className="flex flex-wrap gap-2 mb-3">
                        <CategoryBadge category={playbook.category} />
                        <SeverityBadge severity={playbook.severity} />
                        <Badge variant="secondary">{steps.length} steps</Badge>
                        {!playbook.isActive && <Badge variant="outline">Inactive</Badge>}
                      </div>
                      <div className="text-xs text-muted-foreground space-y-1">
                        <div className="flex justify-between">
                          <span>v{playbook.version}</span>
                          {playbook._count?.executions !== undefined && (
                            <span>{playbook._count.executions} executions</span>
                          )}
                        </div>
                        {playbook.lastUsedAt && (
                          <p>Last used: {new Date(playbook.lastUsedAt).toLocaleDateString()}</p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )
              })}
            </div>
          )}
        </TabsContent>

        {/* Executions Tab */}
        <TabsContent value="executions">
          {executionsLoading ? (
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}
            </div>
          ) : executions.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center py-12">
                <Play className="h-12 w-12 mb-4 opacity-50 text-muted-foreground" />
                <p className="text-lg font-medium text-muted-foreground">No executions yet</p>
                <p className="text-sm text-muted-foreground mt-1">Execute a playbook to see results here.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {executions.map((exec) => {
                const results = (exec.stepResults || []) as StepResult[]
                const completedSteps = results.filter((r) => r.status === 'completed').length

                return (
                  <Card
                    key={exec.id}
                    className="cursor-pointer hover:shadow-md transition-shadow"
                    onClick={() => setSelectedExecutionId(exec.id)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 min-w-0">
                          <ExecutionStatusBadge status={exec.status} />
                          <div className="min-w-0">
                            <p className="font-medium truncate">{exec.playbook?.name}</p>
                            <p className="text-xs text-muted-foreground">
                              {new Date(exec.startedAt).toLocaleString()}
                              {exec.playbook?.category && (
                                <> | {exec.playbook.category.replace('_', ' ')}</>
                              )}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                          <div className="text-right text-xs text-muted-foreground">
                            <p>{completedSteps}/{results.length} steps</p>
                            {exec.completedAt && (
                              <p>{new Date(exec.completedAt).toLocaleString()}</p>
                            )}
                          </div>
                          <ChevronRight className="h-4 w-4 text-muted-foreground" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  )
}
